import React, { useEffect, useState, useCallback } from 'react';
import { Box, Table, Text, Flex } from '@chakra-ui/react';
import SelectField from '../basic/SelectField';
import { PaginationBar } from '../core/PaginationComponents';
import { get } from 'lodash';
import BuyActivityPaneBody from './BuyActivityPaneBody';
import Card, { CardTitle, CardBodyGroup } from '../basic/Card';
import Loading from '../basic/Loading';
import WatermarkText from './WatermarkText';
import { commify, formatMonetaryValue } from '../../functions';
import './BuyActivityStream.scss';
import Constants from '../../Constants';
import moment from 'moment';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

const selectFieldProps = {
  isClearable: true,
  controlStyles: { background: 'none', border: 0, boxShadow: 'none' },
  placeholderStyles: { color: '#979797' },
};

function BuyActivityStream(props) {
  const {
    token,
    configs,
    recentPOs,
    recentPOFilters,
    requesting,
    error,
    users,
    cls,
    inputProducts,
    grinders,
    packerPlants,
    getRecentPOActivity,
  } = props;

  const orderStatus = useSelector(state => state.orderStatusMap.data || {});

  const [filters, setFilters] = useState({
    buyer: null,
    cl: null,
    productId: null,
    productType: Constants.PRODUCT_TYPE_LIST.BEEF,
    fresh_or_frozen: null,
    grinderId: null,
    packerPlantId: null,
    queryType: 'cl',
    currentPage: 1,
    batchSize: 50,
  });

  const {
    buyer,
    cl,
    productId,
    productType,
    fresh_or_frozen,
    grinderId,
    packerPlantId,
    queryType,
    currentPage,
    batchSize,
  } = filters;

  // Fetch data whenever any filter changes
  const fetchData = useCallback(() => {
    getRecentPOActivity({
      buyer,
      cl,
      productId,
      productType,
      fresh_or_frozen,
      grinderId,
      packerPlantId,
      queryType,
      limit: batchSize,
      offset: (currentPage - 1) * batchSize,
      clearOld: currentPage === 1,
    });
  }, [
    buyer,
    cl,
    productId,
    productType,
    fresh_or_frozen,
    grinderId,
    packerPlantId,
    queryType,
    currentPage,
    batchSize,
    getRecentPOActivity,
  ]);

  // Initial load + when token changes
  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  // React to filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page to 1 when filters change (except pagination)
  const updateFilter = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      currentPage: 1, // always go back to page 1 on filter change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, currentPage: page }));
  };

  // === Options for dropdowns ===
  const buyers = get(recentPOFilters, 'filters.orderedByID', []);
  const buyerOptions = [
    ...users
      .filter(u => buyers.includes(u.id))
      .map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` })),
    ...(buyers.includes(null) ? [{ value: 'unknown', label: 'Unknown' }] : []),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const proteinOptions = Constants.PRODUCT_TYPES.filter(p =>
    get(recentPOFilters, 'filters.productType', []).includes(p.value)
  );

  const grinderOptions = [...new Set(grinders.map(g => ({ value: g.uid, label: g.name })))]
    .sort((a, b) => a.label.localeCompare(b.label));

  const establishmentOptions = [...new Set(packerPlants.map(p => ({ value: p.id, label: p.name })))]
    .sort((a, b) => a.label.localeCompare(b.label));

  const clOptions = cls
    .filter(c => get(recentPOFilters, `filters.cl`, []).includes(Number(c)))
    .map(c => ({ value: c, label: c }));

  const productOptions = inputProducts
    .filter(p => get(recentPOFilters, `filters.productName`, []).includes(p.name))
    .map(p => ({ value: p.uid, label: p.name }));

  const targetColumn = queryType === 'cl' ? (
    <SelectField
      value={clOptions.find(o => o.value === cl) || null}
      placeholder="All Lean Points"
      onChange={option => updateFilter({ cl: option ? option.value : null })}
      options={clOptions}
      isSearchable
      isLoading={cls.length === 0}
      {...selectFieldProps}
    />
  ) : (
    <SelectField
      value={productOptions.find(o => o.value === productId) || null}
      placeholder="All Products"
      onChange={option => updateFilter({ productId: option ? option.value : null })}
      options={productOptions}
      isSearchable
      isLoading={inputProducts.length === 0}
      {...selectFieldProps}
    />
  );

  const internalPoLabel = configs?.find(c => c.name === 'internal_po_number')?.value_str || 'Internal PO';

  const renderTable = () => {
    if (!recentPOs?.pos?.length) {
      return <WatermarkText>No POs matching that criteria</WatermarkText>;
    }

    const isCLMode = queryType === 'cl';

    return (
      <Box bgColor="white">
        <Table.Root size="md">
          <Table.Header backgroundColor="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Buyer</Table.ColumnHeader>
              <Table.ColumnHeader>Protein</Table.ColumnHeader>
              <Table.ColumnHeader>{isCLMode ? 'Lean Point' : 'Product'}</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">CIF Buy Price</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Volume</Table.ColumnHeader>
              <Table.ColumnHeader>Establishment</Table.ColumnHeader>
              <Table.ColumnHeader>Grinder</Table.ColumnHeader>
              <Table.ColumnHeader>{internalPoLabel}</Table.ColumnHeader>
              <Table.ColumnHeader>Order Created</Table.ColumnHeader>
              <Table.ColumnHeader>Shipment Date</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Total Price</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {recentPOs.pos.map((po, i) => (
              <Table.Row key={`${i}-${po.orderedOn}-${po.grinderName}`}>
                <Table.Cell>{po.orderedBy || '-'}</Table.Cell>
                <Table.Cell>{po.productType}</Table.Cell>
                <Table.Cell>{isCLMode ? po.cl : po.productName}</Table.Cell>
                <Table.Cell textAlign="right">
                  {formatMonetaryValue(po.buyCurrency, po.buyPricePerUnit, {
                    maximumFractionDigits: 4,
                    minimumFractionDigits: 4,
                  })}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {commify(po.volume)} {po.unitOfMeasure}
                </Table.Cell>
                <Table.Cell>{po.packerPlantName}</Table.Cell>
                <Table.Cell>{po.grinderName}</Table.Cell>
                <Table.Cell>{po.internalPoNumber || '-'}</Table.Cell>
                <Table.Cell>{moment(po.orderedOn).format('DD/MM/YYYY')}</Table.Cell>
                <Table.Cell>{po.shipmentDate || 'None'}</Table.Cell>
                <Table.Cell>{orderStatus[po.status] || po.status || '—'}</Table.Cell>
                <Table.Cell textAlign="right">
                  {formatMonetaryValue(po.buyCurrency, po.total)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        <PaginationBar
          currentPage={currentPage}
          pages={Math.ceil((recentPOs?.total_count || 0) / batchSize)}
          onPageChange={handlePageChange}
          justifyContent="flex-end"
          marginY={6}
          paddingX={5}
        />
      </Box>
    );
  };

  return (
    <Card style={{ height: '100%' }}>
      <CardTitle>
        <Text as="p">Buy Activity Stream</Text>
        <CardBodyGroup gap="20px" marginLeft="30px">
          <Flex gap="20px" color="gray" flexWrap="wrap">
            <Box width="200px">
              <SelectField
                value={buyerOptions.find(o => o.value === buyer) || null}
                placeholder="All Buyers"
                onChange={option => updateFilter({ buyer: option ? option.value : null })}
                options={buyerOptions}
                {...selectFieldProps}
              />
            </Box>

            <Box width="120px">
              <SelectField
                value={proteinOptions.find(o => o.value === productType) || null}
                placeholder="Protein"
                onChange={option => updateFilter({ productType: option ? option.value : null })}
                options={proteinOptions}
                {...selectFieldProps}
              />
            </Box>

            <Box width="140px">
              <SelectField
                value={Constants.FRESH_OR_FROZEN.find(o => o.value === fresh_or_frozen) || null}
                placeholder="Fresh"
                onChange={option => updateFilter({ fresh_or_frozen: option ? option.value : null })}
                options={Constants.FRESH_OR_FROZEN}
                {...selectFieldProps}
              />
            </Box>

            <Box width="240px">{targetColumn}</Box>

            <Box width="240px">
              <SelectField
                value={grinderOptions.find(o => o.value === grinderId) || null}
                placeholder="Grinder"
                onChange={option => updateFilter({ grinderId: option ? option.value : null })}
                options={grinderOptions}
                {...selectFieldProps}
              />
            </Box>

            <Box width="240px">
              <SelectField
                value={establishmentOptions.find(o => o.value === packerPlantId) || null}
                placeholder="Establishment"
                onChange={option => updateFilter({ packerPlantId: option ? option.value : null })}
                options={establishmentOptions}
                {...selectFieldProps}
              />
            </Box>
          </Flex>
        </CardBodyGroup>
      </CardTitle>

      <BuyActivityPaneBody>
        {(requesting || !configs) && <Loading />}
        {error && <WatermarkText>Could not load recent buy activity</WatermarkText>}
        {!requesting && configs && recentPOs && renderTable()}
      </BuyActivityPaneBody>
    </Card>
  );
}

BuyActivityStream.propTypes = {
  token: PropTypes.string,
  configs: PropTypes.array,
  recentPOs: PropTypes.object,
  recentPOFilters: PropTypes.object,
  requesting: PropTypes.bool,
  error: PropTypes.bool,
  users: PropTypes.array,
  cls: PropTypes.array,
  inputProducts: PropTypes.array,
  grinders: PropTypes.array,
  packerPlants: PropTypes.array,
  getRecentPOActivity: PropTypes.func.isRequired,
};

export default BuyActivityStream;
